import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Receipt, TrendingDown, Brain, Search, ArrowRight, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const TaxOptimizationPage = () => {
  const benefits = [
    {
      icon: TrendingDown,
      title: "Reduce Tax Outgo Without Cutting Corners",
      description: "With evolving tax laws, most people either underutilize benefits or depend too much on last-minute hacks. Our approach:",
      points: [
        "Maximize deductions under Sections 80C, 80D, 24(b), etc.",
        "Utilize tax-saving investment products strategically",
        "Reduce capital gains tax impact across your portfolio"
      ]
    },
    {
      icon: Brain,
      title: "Integrated with Your Overall Financial Plan",
      description: "Tax-saving isn't a separate event — it must align with your:",
      points: [
        "Investment strategy and time horizon",
        "Health and life insurance planning",
        "Retirement and legacy goals"
      ]
    },
    {
      icon: Search,
      title: "Audit-Proof and Compliant Approach",
      description: "Our tax planning strategies are fully compliant with Indian income tax regulations — no shady tricks, no future regrets. We help you:",
      points: [
        "Maintain clear documentation and records",
        "Avoid red flags that trigger audits or scrutiny",
        "Use every available benefit without bending rules"
      ]
    }
  ];

  const taxSavingOptions = [
    { section: "80C", limit: "₹1.5 Lakh", description: "ELSS, PPF, Life Insurance, NSC, 5-yr FD" },
    { section: "80D", limit: "₹25K-75K", description: "Health Insurance Premium" },
    { section: "80CCD(1B)", limit: "₹50,000", description: "NPS Additional Contribution" },
    { section: "24(b)", limit: "₹2 Lakh", description: "Home Loan Interest" },
    { section: "80E", limit: "No Limit", description: "Education Loan Interest" },
    { section: "80G", limit: "50-100%", description: "Donations to Eligible Charities" }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 mb-6">
                <Receipt className="w-4 h-4 text-cyan-400" />
                <span className="text-white/60 text-sm">Tax Optimization</span>
              </div>

              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Maximize Returns, Minimize Liability
              </h1>

              <p className="text-lg text-white/60 leading-relaxed mb-6">
                Paying taxes is inevitable. Overpaying isn't. Smart tax planning means keeping more of what you earn while staying compliant. It's not just about deductions — it's about building an integrated financial plan that aligns investments, insurance, and expenses to reduce your tax burden.
              </p>

              <p className="text-white/80 mb-8">
                At <span className="text-cyan-400 font-semibold">Frontier Investments</span>, we help you legally optimize taxes while growing your wealth, protecting your family, and planning long-term goals — all in one strategic move.
              </p>

              <Link
                to="/contact"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black font-semibold rounded-full hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-all duration-500 group"
              >
                Optimize Your Taxes
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=600&fit=crop"
                alt="Tax Optimization"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div
                  key={index}
                  className="p-8 bg-gray-50 rounded-3xl border border-gray-100 hover:shadow-xl transition-all duration-500"
                >
                  <div className="p-4 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl w-fit mb-6">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-black mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 mb-6">{benefit.description}</p>
                  <ul className="space-y-3">
                    {benefit.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="p-1 bg-cyan-100 rounded-full mt-1">
                          <Check className="w-3 h-3 text-cyan-600" />
                        </div>
                        <span className="text-gray-600 text-sm">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tax Saving Options */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Key Tax-Saving Sections
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Maximize your tax benefits under various sections of the Income Tax Act
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {taxSavingOptions.map((option, index) => (
              <div
                key={index}
                className="p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-500"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-2xl font-bold text-cyan-400">Section {option.section}</span>
                  <span className="px-3 py-1 bg-cyan-500/20 rounded-full text-cyan-300 text-sm">{option.limit}</span>
                </div>
                <p className="text-white/60">{option.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6">
            Ready to optimize your taxes?
          </h2>
          <p className="text-gray-500 mb-10">
            Let our experts create a tax-efficient investment strategy for you
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-black text-white font-semibold rounded-full hover:shadow-lg transition-all duration-500 group"
          >
            Book a Consultation
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TaxOptimizationPage;
